import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ExpandMore } from '@mui/icons-material';
import AccordionDetails from './accordion-details';
import { Accordion, Grid, Typography } from '@mui/material';
import TableFunctionality from './table';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { Functionality as FunctionalityM } from 'shell/models/functionality';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { HeadCell } from 'shell/components/shared/table/types';
import { FunctionalityVM } from 'system/view-models/functionality';
import { PmiButton } from 'shell/components/shared/button';

const sortList = (list: Array<FunctionalityVM>) =>
  list.sort((a, b) => {
    const aEntity = a.entity || '';
    const bEntity = b.entity || '';

    if (aEntity < bEntity) {
      return -1;
    }

    if (a.description < b.description) {
      return -1;
    }

    return 1;
  });

export const headFunctionality: HeadCell<FunctionalityVM, keyof FunctionalityVM>[] = [
  {
    id: 'id',
    label: 'ID',
  },
  {
    id: 'entity',
    label: 'Entità',
  },
  {
    id: 'description',
    label: 'Descrizione',
  },
  {
    id: 'action',
    label: '',
  },
];

interface FunctionalityDetailProps {
  onChange: (functionalities: Array<FunctionalityVM>) => void;
  functionalities: Array<FunctionalityVM>;
  disabled?: boolean;
  insert?: boolean;
}

const FunctionalityComponent = ({
  onChange,
  functionalities,
  disabled,
  insert,
}: FunctionalityDetailProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(!insert);

  const {
    data,
    get: getFunctions,
    error,
  } = useHttpFetch<HttpResponseList<FunctionalityM>>(`${__MANAGEMENT_API_URL__}/funzioni`, {
    data: {},
  });

  const functionsSaved: Array<FunctionalityVM> = useMemo(() => {
    const operations = data?.payload;
    if (!operations || !!error) {
      return [];
    }
    const list: Array<FunctionalityVM> = operations.list
      .map((functionality) => {
        const newFunction = {
          id: functionality.key.codice.toString(),
          description: functionality.nome,
          institutionCode: functionality.codiceEnte,
          event: functionality.evento,
          entity: functionality.entita,
        };

        const isSelectedFunction = !!functionalities.find(
          (operation) => operation.id === newFunction.id.toString()
        );

        return {
          ...newFunction,
          checked: isSelectedFunction,
        };
      })
      .filter((item) => item.checked)
      .map((item) => ({ ...item, checked: false }));

    return sortList(list);
  }, [data, functionalities]);

  const functionsNotSelected: Array<FunctionalityVM> = useMemo(() => {
    const operations = data?.payload;
    if (!operations || !!error) {
      return [];
    }

    const list: Array<FunctionalityVM> = operations.list
      .map((functionality) => {
        const newFunction = {
          id: functionality.key.codice.toString(),
          description: functionality.nome,
          institutionCode: functionality.codiceEnte,
          event: functionality.evento,
          entity: functionality.entita,
        };

        const isSelectedFunction = !!functionalities.find(
          (operation) => operation.id === newFunction.id.toString()
        );

        return {
          ...newFunction,
          checked: isSelectedFunction,
        };
      })
      .filter((item) => !item.checked);

    return sortList(list);
  }, [data, functionalities]);

  useEffect(() => {
    getFunctions();
  }, []);

  const [functionsSavedState, setFunctionsSavedState] =
    useState<Array<FunctionalityVM>>(functionsSaved);

  const [functionsNotSelectedState, setFunctionsNotSelectedState] =
    useState<Array<FunctionalityVM>>(functionsNotSelected);

  useEffect(() => {
    setFunctionsSavedState(functionsSaved);
  }, [functionsSaved]);

  useEffect(() => {
    setFunctionsNotSelectedState(functionsNotSelected);
  }, [functionsNotSelected]);

  const onCheckPressSaved = useCallback(
    (functionalitySelected) => {
      const functionalitiesNew = functionsSavedState.slice().map((functionality) =>
        functionality.id === functionalitySelected.id
          ? {
              ...functionality,
              checked: !functionality.checked,
            }
          : functionality
      );

      setFunctionsSavedState(functionalitiesNew);
    },
    [setFunctionsSavedState, functionsSavedState]
  );

  const onCheckPressNotSelected = useCallback(
    (functionalitySelected) => {
      const functionalitiesNew = functionsNotSelectedState.slice().map((functionality) =>
        functionality.id === functionalitySelected.id
          ? {
              ...functionality,
              checked: !functionality.checked,
            }
          : functionality
      );

      setFunctionsNotSelectedState(functionalitiesNew);
    },
    [setFunctionsNotSelectedState, functionsNotSelectedState]
  );

  const onSelectAllNotSelected = useCallback(
    (selected) => {
      const functionalitiesNew = functionsNotSelectedState.slice().map((functionality) => ({
        ...functionality,
        checked: selected,
      }));

      setFunctionsNotSelectedState(functionalitiesNew);
    },
    [setFunctionsNotSelectedState, functionsNotSelectedState]
  );

  const onSelectAllSaved = useCallback(
    (selected) => {
      const functionalitiesNew = functionsSavedState.slice().map((functionality) => ({
        ...functionality,
        checked: selected,
      }));

      setFunctionsSavedState(functionalitiesNew);
    },
    [setFunctionsSavedState, functionsSavedState]
  );

  const handleCheckedRight = useCallback(() => {
    const functionsNotSelectedOld = functionsNotSelectedState
      .slice()
      .filter((item) => item.checked);

    const functionsSavedUpdated = functionsSavedState.concat(functionsNotSelectedOld);

    const newNotSelectedFunctions = functionsNotSelectedState
      .slice()
      .filter((item) => !item.checked);

    setFunctionsNotSelectedState(sortList(newNotSelectedFunctions));

    setFunctionsSavedState(sortList(functionsSavedUpdated));

    onChange(sortList(functionsSavedUpdated));
  }, [
    onChange,
    setFunctionsSavedState,
    setFunctionsNotSelectedState,
    functionsSavedState,
    functionsNotSelectedState,
  ]);

  const handleCheckedLeft = useCallback(() => {
    const functionsSavedOld = functionsSavedState.slice().filter((item) => item.checked);

    const functionsNotSelectedUpdated = functionsNotSelectedState.concat(functionsSavedOld);

    setFunctionsNotSelectedState(sortList(functionsNotSelectedUpdated));

    const newSavedFunctions = functionsSavedState.slice().filter((item) => !item.checked);

    setFunctionsSavedState(sortList(newSavedFunctions));

    onChange(sortList(newSavedFunctions));
  }, [
    onChange,
    setFunctionsSavedState,
    setFunctionsNotSelectedState,
    functionsSavedState,
    functionsNotSelectedState,
  ]);

  return (
    <div className="pmi-pt-2">
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
      >
        <AccordionSummary
          aria-controls="panel-groups-content"
          id="panel-groups-header"
          classes={{
            root: 'pmi-flex-row-reverse',
          }}
          expandIcon={<ExpandMore />}
        >
          <Typography component="h6">Lista Funzionalità</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1} justifyContent="center">
            <Grid item xs>
              <Typography component="h6">Disponibili</Typography>
              <TableFunctionality
                minWidth={600}
                head={headFunctionality}
                disabled={disabled}
                insert={insert}
                body={functionsNotSelectedState?.filter(() => !disabled) || []}
                onCheckPress={onCheckPressNotSelected}
                onSelectAll={onSelectAllNotSelected}
              />
            </Grid>
            <Grid item display="flex">
              <Grid container direction="column" justifyContent="center" display="flex">
                <PmiButton
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  onClick={handleCheckedRight}
                  disabled={functionsNotSelectedState.length === 0 || disabled}
                  aria-label="move selected right"
                >
                  &gt;
                </PmiButton>
                <PmiButton
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  onClick={handleCheckedLeft}
                  disabled={functionsSavedState.length === 0 || disabled}
                  aria-label="move selected left"
                >
                  &lt;
                </PmiButton>
              </Grid>
            </Grid>
            <Grid item xs>
              <Typography component="h6">Scelte</Typography>
              <TableFunctionality
                minWidth={600}
                head={headFunctionality}
                disabled={disabled}
                insert={insert}
                body={
                  functionsSavedState.map((item) => ({
                    ...item,
                    checked: disabled ? true : item.checked,
                  })) || []
                }
                onCheckPress={onCheckPressSaved}
                onSelectAll={onSelectAllSaved}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};
FunctionalityComponent.displayName = 'Group detail component';

const Functionality = memo(FunctionalityComponent);

export default Functionality as typeof FunctionalityComponent;
